/**
 * Sofa designs — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_home_designs`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const sofaDesignsPack: ToilePack = {
    slug: 'sofa-designs',
    category: 'home',
    name: 'Sofa designs',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'lshape', label: 'L-shape', subject: 'an L-shaped fabric sofa in a living room'},
        {key: 'chesterfield', label: 'Chesterfield', subject: 'a tufted velvet Chesterfield sofa'},
        {key: 'wood', label: 'Wooden', subject: 'a teak wooden sofa with cushions'},
    ],
    styles: [
        {key: 'modern', label: 'Modern', style: 'modern living room with a textured rug, a low coffee table and a large window, soft daylight from camera-left with gentle shadows, a bright balance across the room', palette: 'neutral beige and grey with one accent color in the cushions', framing: 'eye-level wide shot with the sofa centred'},
        {key: 'heritage', label: 'Heritage', style: 'heritage living room with a carved jaali screen, brass decor and a handwoven dhurrie, warm afternoon sun from camera-right through wooden shutters, a warm balance of sunlit patches and shade', palette: 'rust, mustard, teak and brass', framing: 'three-quarter view from the corner of the room'},
        {key: 'evening', label: 'Evening', style: 'cosy evening living room with a warm cove glow and indoor plants, dusk blue falling from behind through the window, a moody balance of warm glow and soft shadow', palette: 'deep green, walnut and warm amber', framing: 'slightly low view at seat height'},
    ],
    directions: ['small room', 'white sofa', 'with recliner'],
    directionHint: 'try: small room',
    seo: {
        title: 'Sofa designs: L-shape, Chesterfield and wooden',
        metaDescription: 'Nine sofa pictures: L-shape, Chesterfield and wooden sofas, each in a modern, heritage or evening living room.',
        h1: 'Sofa designs: L-shape, Chesterfield and wooden',
        tagline: 'Nine pictures, three sofa shapes, three living rooms each: modern, heritage and evening.',
        intro: 'Three sofa shapes to order from a carpenter: an L-shaped fabric sofa, a tufted velvet Chesterfield, and a teak wooden sofa with cushions. Each is shown in a modern living room with a textured rug, in a heritage room with carved jaali and brass, and in an evening room with warm light. Type a different room, such as a small room, for a variation on the same three shapes.',
        useCases: [
            'Briefing a carpenter on a sofa shape',
            'A sofa for a small living room',
            'A velvet Chesterfield for a formal room',
            'A teak wooden sofa for a heritage room',
        ],
        faq: [
            {q: 'What is the difference between the three rooms?', a: 'The modern room uses neutral beige and grey with one accent colour in the cushions. The heritage room uses rust, mustard, teak and brass. The evening room uses deep green, walnut and warm amber.'},
            {q: 'Do the pictures show seat sizes or heights?', a: 'No. They show the look of each sofa in a room, with no dimensions. Measure your room and the doorways before you order.'},
            {q: 'Are these real sofas?', a: 'No. They are generated images styled to look like photographs of Indian living rooms.'},
            {q: 'Can I ask for a sofa with a recliner?', a: 'Yes. Type it as a direction. The result is a variation on the same three shapes, not a guaranteed match.'},
        ],
    },
}
